import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import defaultFormState, { requiredFields } from '@models/brandColour'

import PageContext from '@contexts/pageContext'

import * as brandColourActions from '@redux/modules/brandColour'
import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'

import type { BrandColourModel } from '@models/types'

const stringifyObjectKeys = ['cmyk', 'rgb']

type CreateBrandColourParams = {
  brandColourParams: Partial<BrandColourModel>,
  dispatch: AppDispatch,
}

const createBrandColour = (params: CreateBrandColourParams) => {
  const { dispatch, brandColourParams } = params
  const { createBrandColour: createFn } = brandColourActions

  // @ts-expect-error TS2345
  const updatedParams = mergeAndStringifyEntityObjects({}, brandColourParams, stringifyObjectKeys)

  return dispatch(createFn(updatedParams))
}

type UpdateBrandColourParams = {
  brandColour: BrandColourModel,
  brandColourParams: Partial<BrandColourModel>,
  dispatch: AppDispatch,
}

const updateBrandColour = (params: UpdateBrandColourParams) => {
  const {
    dispatch, brandColour, brandColourParams,
  } = params
  const { updateBrandColour: updateFn } = brandColourActions

  // @ts-expect-error TS2345
  const updatedParams = mergeAndStringifyEntityObjects(brandColour, brandColourParams, stringifyObjectKeys)

  return dispatch(updateFn(updatedParams))
}

type DeleteBrandColourParams = {
  brandColour: DeleteParams<BrandColourModel>,
  dispatch: AppDispatch,
}

const deleteBrandColour = (params: DeleteBrandColourParams) => {
  const { dispatch, brandColour } = params
  const { deleteBrandColour: deleteFn } = brandColourActions

  return dispatch(deleteFn(brandColour))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useBrandColourForm(
  brandColour: Partial<BrandColourModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const brandColourForm = useForm(
    defaultFormState,
    { entity: brandColour, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [brandColour.id, brandColour.cache_key],
  )

  return {
    ...brandColourForm,
  }
}

export const useRelations = (brandColour: Partial<BrandColourModel> = {}) => {
  const { brand_id } = brandColour

  const brands = useSelector(reduxState => reduxState.entities.brands)

  const brand = brand_id ? brands[brand_id] || {} : {}

  return {
    brand,
  }
}

function useBrandColour(initEntity: Partial<BrandColourModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: brandColour }: { entity: BrandColourModel } = useLatestEntity(initEntity, 'brandColours')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    creating, deleting, loading, updating,
  } = useSelector(reduxState => reduxState.brandColours)

  return {
    brandColour,
    callbacks: {
      createBrandColour: (brandColourParams: Partial<BrandColourModel>) => (
        createBrandColour({ brandColourParams, dispatch })
      ),
      createOrEditBrandColour: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditBrandColourModal',
        payload: { brandColour },
      }),
      deleteBrandColour: () => deleteBrandColour({ dispatch, brandColour }),
      updateBrandColour: (brandColourParams: Partial<BrandColourModel>) => (
        updateBrandColour({
          brandColour, brandColourParams, dispatch,
        })
      ),
    },
    creating,
    deleting,
    loading,
    updating,
  }
}

export default useBrandColour
